import { Injectable } from '@angular/core';
import {
    Firestore, collection, addDoc, updateDoc, deleteDoc,
    doc, getDocs, getDoc, where, query,
    orderBy, arrayUnion, collectionData, limit
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IRequest } from '../../../shared/models/users/request.model';
import { IBasicUser, IUser } from '../../../shared/models/users/user.model';
import { UserProfileService } from '../../../shared/services/user-profile.service';
import { Statuses } from '../../../shared/models/users/constants/status';
import { Roles } from '../../../shared/models/users/constants/roles';


@Injectable({
    providedIn: 'root'
})
export class RequestService {
    private readonly collectionName = 'requests';
    private currentUser$!: IUser | null
    constructor(
        private firestore: Firestore,
        private currentUserService: UserProfileService

    ) {
        this.currentUser$ = this.currentUserService.getUserProfile();
    }

    async createRequest<T>(request: IRequest<T>): Promise<string> {
        const requestCollection = collection(this.firestore, this.collectionName);
        request.createdAt = new Date();
        request.createdBy =
        {
            id: this.currentUser$?.id,
            email: this.currentUser$?.email,
            role: this.currentUser$?.role,
            name: this.currentUser$?.firstName + " " + this.currentUser$?.lastName
        }
        const docRef = await addDoc(requestCollection, request);
        return docRef.id;
    }

    async getRequestById<T>(id: string): Promise<IRequest<T> | null> {
        const requestDoc = doc(this.firestore, this.collectionName, id);
        const docSnap = await getDoc(requestDoc);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() } as IRequest<T>;
        }
        return null;
    }

    async getAllRequests<T>(): Promise<IRequest<T>[]> {
        const requestCollection = collection(this.firestore, this.collectionName);
        const q = query(requestCollection, orderBy('createdAt', 'desc'));

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as IRequest<T>[];
    }

    async getMyRequests<T>(createdByEmail: string): Promise<IRequest<T>[]> {
        const requestCollection = collection(this.firestore, this.collectionName);

        const q = query(requestCollection, where('createdBy.email', '==', createdByEmail));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as IRequest<T>);
    }

    async updateRequest<T>(id: string, updates: Partial<IRequest<T>>): Promise<void> {
        const requestDoc = doc(this.firestore, this.collectionName, id);
        updates.updatedAt = new Date();
        updates.updatedBy =
        {
            id: this.currentUser$?.id,
            email: this.currentUser$?.email,
            role: this.currentUser$?.role,
            name: this.currentUser$?.firstName + " " + this.currentUser$?.lastName
        }
        await updateDoc(requestDoc, updates as any);
    }

    async sendRequest<T>(id: string): Promise<void> {

        // send request

        // update status
        const requestDoc = doc(this.firestore, this.collectionName, id);
        await updateDoc(requestDoc, {
            status: Statuses.PENDING,
            submissionDate: new Date(),
            approved: null,
            approvedAt: null,
            approvedBy: null
        } as any);

        // send email
    }

    async deleteRequest(id: string): Promise<void> {
        const requestDoc = doc(this.firestore, this.collectionName, id);
        await deleteDoc(requestDoc);
    }

    async approveRequest(id: string, approvedBy: IBasicUser): Promise<void> {
        const requestDoc = doc(this.firestore, this.collectionName, id);
        // Remove undefined values from approvedBy object
        const cleanApprovedBy = Object.fromEntries(
            Object.entries(approvedBy).filter(([_, value]) => value !== undefined)
        );
        await updateDoc(requestDoc, {
            status: Statuses.APPROVED,
            approved: true,
            approvedAt: new Date(),
            approvedBy: cleanApprovedBy
        });
    }

    async rejectRequest(id: string): Promise<void> {
        const requestDoc = doc(this.firestore, this.collectionName, id);
        await updateDoc(requestDoc, {
            status: Statuses.REJECTED,
            approved: false,
            approvedAt: new Date()
        });
    }

    async signRequest(id: string, signedBy: string): Promise<void> {
        const requestDoc = doc(this.firestore, this.collectionName, id);
        await updateDoc(requestDoc, {
            status: 'signed',
            signed: true,
            signedAt: new Date(),
            signedBy: signedBy
        });
    }

    async requestCount(): Promise<number> {
        const requestsCollection = collection(this.firestore, this.collectionName);
        const requestsSnapshot = await getDocs(requestsCollection);
        return requestsSnapshot.size;
    }

    async requestMaxCompanyCount(): Promise<{ maxCompanyName: string; company: string; maxCompanyCount: number; totalRequests: number; }> {
        const requestsCollection = collection(this.firestore, this.collectionName);
        const requestsSnapshot = await getDocs(requestsCollection);
        const companyCounts = new Map<string, number>();
        var companyNames: Record<string, string> = {};
        requestsSnapshot.forEach(doc => {
            const request: IRequest<any> = doc.data() as IRequest<any>;
            const code = request.company?.code;

            if (code) {
                companyNames[code]= request.company?.name || '';
                companyCounts.set(code, (companyCounts.get(code) || 0) + 1);
            }
        });
        const maxCount = Math.max(...companyCounts.values(), 0);
        const maxCompany = Array.from(companyCounts.entries()).reduce((a, b) => a[1] > b[1] ? a : b)[0];
        const maxCompanyName = companyNames[maxCompany];
        return { maxCompanyName: maxCompanyName, company: maxCompany, maxCompanyCount: maxCount, totalRequests: requestsSnapshot.size };


    }


    getUnreadRequests(role: string, userId: string): Observable<IRequest<any>[]> {
        const requestsCollection = collection(this.firestore, this.collectionName);
        let q;

        if (role === Roles.HR || role === Roles.ADMIN) {
            // HR and Admin see all unread requests
            q = query(
                requestsCollection,
                orderBy('createdAt', 'desc'),
                limit(50)
            );
        } else if (role === Roles.ACCOUNTANT) {
            // Accountant sees only Equipment and Financial requests
            q = query(
                requestsCollection,
                where('type', 'in', ['Equipment', 'Financial']),
                orderBy('createdAt', 'desc'),
                limit(50)
            );
        } else {
            // Regular employees see only reminders for their own requests when Approved or Rejected
            q = query(
                requestsCollection,
                where('createdBy.id', '==', userId),
                where('status', 'in', [Statuses.APPROVED, Statuses.REJECTED]),
                limit(50)
            );
        }

        return collectionData(q, { idField: 'id' }).pipe(
            map((data: any[]) => {
                const requests = data as IRequest<any>[];
                // Additional filter for unread status
                return requests.filter(req => !req.readBy?.includes(userId));
            })
        );
    }

    async markAsRead(id: string, userId: string): Promise<void> {
        const requestDoc = doc(this.firestore, this.collectionName, id);
        await updateDoc(requestDoc, {
            readBy: arrayUnion(userId)
        });
    }

}
